import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, modalStore, ModalV1 } from '@feathr/components';

import * as styles from './Articles.css';

// Attribute set is the polo opt-out contract; change only together with anhinga's consent cookie.
export const OPT_OUT_COOKIE =
  'do_not_collect=1; domain=.feathr.co; path=/; max-age=31536000; Secure; SameSite=None';

function FAQ() {
  const { t } = useTranslation();
  const [optOutSucceeded, setOptOutSucceeded] = useState(false);

  function handleOptOutClick(): void {
    document.cookie = OPT_OUT_COOKIE;
    setOptOutSucceeded(/(?:^|;\s*)do_not_collect=1(?:;|$)/.test(document.cookie));
    modalStore.isModalOpen = true;
  }

  return (
    <div>
      <h1>Frequently Asked Questions</h1>
      <div className={styles.infoBlock}>
        <h2>What is Feathr?</h2>
        <p>
          Feathr is a business to business SaaS company, which sells a Digital Marketing Toolkit
          designed specifically for associations and organizations that host live events. The Feathr
          application allows its customers to collect web analytics data on their audience.
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>How does Feathr collect web analytics data?</h2>
        <p>
          Cookies are small text files that web servers typically send to a user's computer when
          they visit a website. Cookies are stored as text files on the user's hard drive and can be
          accessed by web servers when the user visits a website or views an advertisement. Cookies
          can be used by companies as a way to identify a browser across multiple sessions.
        </p>
        <p>
          Feathr uses the standard “cookies” feature of major browser applications that allows us to
          store a small piece of data on a user's computer. These cookies allow Feathr to uniquely
          identify a browser across multiple web sessions up to 365 days apart. Feathr also uses a
          1×1 gif also known as a web-beacon, along with cookies, to capture web analytics data of
          the user interacting with our customer's website and other web content. This web-beacon
          sends non-personally identifiable information (non-PII) such as URL, time of visit, and
          screen size, to Feathr so we can provide insight to our customers on their audience's
          interactions with their online content.
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>What cookies does Feathr store?</h2>
        <ul>
          <li>
            <span className={styles.rowLabel}>Cookie Name:</span>f_id
            <ul>
              <li>
                <span className={styles.rowLabel}>Domain:</span>feathr.co
              </li>
              <li>
                <span className={styles.rowLabel}>Description:</span>A 24 character hexadecimal text
                value stored for the purpose of browser identification across sessions.
              </li>
              <li>
                <span className={styles.rowLabel}>Expiry:</span>365 days after the last visit to web
                content serviced by Feathr.
              </li>
            </ul>
          </li>
          <li>
            <span className={styles.rowLabel}>Cookie Name:</span>feathr_session_id
            <ul>
              <li>
                <span className={styles.rowLabel}>Domain:</span>The website you are visiting.
              </li>
              <li>
                <span className={styles.rowLabel}>Description:</span>A 24 character hexadecimal text
                value stored for relating a set of web activity with a web session.
              </li>
              <li>
                <span className={styles.rowLabel}>Expiry:</span>1 hour after the last visit to web
                content serviced by Feathr.
              </li>
            </ul>
          </li>
          <li>
            <span className={styles.rowLabel}>Cookie Name:</span>do_not_collect
            <ul>
              <li>
                <span className={styles.rowLabel}>Domain:</span>feathr.co
              </li>
              <li>
                <span className={styles.rowLabel}>Description:</span>A flag set when you opt out on
                this page. Feathr's web analytics record nothing from requests that carry it.
              </li>
              <li>
                <span className={styles.rowLabel}>Expiry:</span>Requested for 365 days from opt-out;
                Safari expires script-set cookies after about 7 days.
              </li>
            </ul>
          </li>
          <li>
            <span className={styles.rowLabel}>Cookie Name:</span>do_not_track
            <ul>
              <li>
                <span className={styles.rowLabel}>Domain:</span>feathr.co
              </li>
              <li>
                <span className={styles.rowLabel}>Description:</span>The same opt-out flag, set by
                Feathr's server when you visit{' '}
                <a href={'https://blackbox.feathr.co/consent/'}>blackbox.feathr.co/consent</a>. Both
                flags work the same.
              </li>
              <li>
                <span className={styles.rowLabel}>Expiry:</span>365 days after it is set;
                script-cookie limits do not apply to server-set cookies.
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div className={styles.infoBlock}>
        <h2>What does Feathr collect?</h2>
        <div className={styles.collectedData}>
          By default, Feathr will only collect anonymous web behavioral data. The following list is
          an example of some of the data that Feathr collects by default.
          <ul>
            <li>
              <label>Created:</label>The time a web activity took place.
            </li>
            <li>
              <label>Creative Id:</label>A unique identifier to show what web content was served.
            </li>
            <li>
              <label>Session Id:</label>A unique identifier to connect a series of web activity to a
              session
            </li>
            <li>
              <label>Flavor:</label>A descriptor of the type of web activity. Example "Page View",
              and "Ad View"
            </li>
            <li>
              <label>Location URL:</label>The URL that the web activity took place on.
            </li>
            <li>
              <label>IP Address:</label>The IP of the device performing web activity.
            </li>
            <li>
              <label>Screen & Browser Dimensions:</label>Width and height of browser window, and
              screen.
            </li>
            <li>
              <label>Platform:</label>Operating system of device
            </li>
            <li>
              <label>Browser:</label>Chrome, Safari, IE... etc.
            </li>
          </ul>
        </div>
        <p>
          In addition to the default values collected by Feathr, our customers have the ability to
          send us additional data which may contain PII. Some examples of common PII our customers
          provide is email, name, and company
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>What does Feathr do with collected data?</h2>
        <p>
          The data collected by Feathr is processed so that our customers can segment their
          audiences into more specific groups which have expressed interest in their association or
          event. This is done for the purpose of their marketing efforts and provides targetable
          audiences for digital marketing campaigns.
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>Who does Feathr provide my data to?</h2>
        <p>
          All data that is collected and processed by Feathr is privately shared between Feathr and
          the customer that provided the unprocessed data to Feathr. Feathr does not sell or release
          personally identifiable information to any third party vendors or data processors.
        </p>
        <p>
          Additionally, Feathr may disclose personal information in response to lawful requests by
          public authorities, including to meet national security or law enforcement requirements.
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>Can I see, correct, and/or delete the info Feathr has about me?</h2>
        <p>
          Yes - For privacy reasons we require proof of identity before sharing PII. To get the
          process started please email <strong>privacy@feathr.co</strong>
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>How can I opt-out?</h2>
        <p>
          Simply{' '}
          <Button onClick={handleOptOutClick} type={'link'}>
            click here
          </Button>{' '}
          and we set an opt-out cookie in this browser. Feathr's web analytics record nothing from
          requests that carry it — no page views, ad views, or link clicks. Tracked links still
          work.
        </p>
        <p>
          The cookie lasts up to 365 days and applies only to this browser on this device. It ends
          when you clear cookies, and Safari expires script-set cookies after about 7 days. It does
          not cover forms you submit or email opens and clicks — for email, use the unsubscribe
          link.
        </p>
        <p>
          Safari and Firefox block cross-site cookies by default, so the cookie may never reach
          Feathr there. Turn on the Global Privacy Control (GPC) signal instead: it is a request
          header, not a cookie, and Feathr honors it on every request.
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>How can I contact Feathr for inquiries or complaints?</h2>
        <p>
          Email <strong>privacy@feathr.co</strong> and we will respond as quickly as possible.
        </p>
      </div>
      <div className={styles.infoBlock}>
        <h2>Where can I find Feathr's official privacy policies?</h2>
        <p>
          Our official Privacy Policy can be found at{' '}
          <a href={'https://www.feathr.co/privacy-policy'}>www.feathr.co/privacy-policy</a>
        </p>
      </div>
      <ModalV1 cancelButtonText={'Close'} t={t}>
        {optOutSucceeded ? (
          <>
            <h1>Opt-out cookie set</h1>
            <div>
              An opt-out cookie is now stored in this browser. Feathr's web analytics record nothing
              from requests that carry it. Safari and Firefox block cross-site cookies by default —
              turn on the Global Privacy Control (GPC) signal there too.
            </div>
          </>
        ) : (
          <>
            <h1>Cookie not saved</h1>
            <div>
              Your browser did not accept the opt-out cookie — it may be set to block cookies. Allow
              cookies and try again, or turn on the Global Privacy Control (GPC) signal; Feathr
              honors it the same way.
            </div>
          </>
        )}
      </ModalV1>
    </div>
  );
}

export default FAQ;
